import { useState, useEffect } from "react";
import { 
  Ticket, Clock, CheckCircle, XCircle, Copy, Tag,
  Calendar, Euro, Percent, Gift, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VouchersPage({ user }) {
  const [loading, setLoading] = useState(true);
  const [vouchers, setVouchers] = useState([]);
  const [filter, setFilter] = useState("active"); // active, used, expired, all

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    try {
      const res = await authFetch(`${API}/vouchers`, { });
      if (res.ok) {
        setVouchers(await res.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento voucher");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Codice copiato negli appunti!");
  };

  const getStatusBadge = (voucher) => {
    if (voucher.used) {
      return <Badge className="bg-zinc-600 text-zinc-300"><CheckCircle className="w-3 h-3 mr-1" /> Utilizzato</Badge>;
    }
    
    const expiresAt = new Date(voucher.expires_at);
    if (expiresAt < new Date()) {
      return <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" /> Scaduto</Badge>;
    }
    
    const daysLeft = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 7) {
      return <Badge className="bg-yellow-500/20 text-yellow-400"><Clock className="w-3 h-3 mr-1" /> Scade tra {daysLeft}g</Badge>;
    }
    
    return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" /> Attivo</Badge>;
  };

  const getVoucherIcon = (type) => {
    switch (type) {
      case "discount_percent":
        return <Percent className="w-6 h-6 text-blue-400" />;
      case "voucher":
        return <Euro className="w-6 h-6 text-green-400" />;
      default:
        return <Gift className="w-6 h-6 text-purple-400" />;
    }
  };

  const filteredVouchers = vouchers.filter(v => {
    if (filter === "all") return true;
    if (filter === "active") {
      return !v.used && new Date(v.expires_at) > new Date();
    }
    if (filter === "used") return v.used;
    if (filter === "expired") {
      return !v.used && new Date(v.expires_at) <= new Date();
    }
    return true;
  });

  const activeCount = vouchers.filter(v => !v.used && new Date(v.expires_at) > new Date()).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white uppercase font-['Barlow_Condensed'] tracking-wider flex items-center gap-2">
            <Ticket className="w-6 h-6 text-primary" />
            I Miei Voucher
          </h1>
          <p className="text-zinc-400 text-sm">
            {activeCount} voucher attivi disponibili
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: "active", label: "Attivi", count: vouchers.filter(v => !v.used && new Date(v.expires_at) > new Date()).length },
          { key: "used", label: "Utilizzati", count: vouchers.filter(v => v.used).length },
          { key: "expired", label: "Scaduti", count: vouchers.filter(v => !v.used && new Date(v.expires_at) <= new Date()).length },
          { key: "all", label: "Tutti", count: vouchers.length }
        ].map(f => (
          <Button
            key={f.key}
            variant={filter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.key)}
            className="gap-2"
          >
            {f.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              filter === f.key ? "bg-white/20" : "bg-zinc-700"
            }`}>
              {f.count}
            </span>
          </Button>
        ))}
      </div>

      {/* Vouchers List */}
      {filteredVouchers.length === 0 ? (
        <div className="text-center py-12">
          <Ticket className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            {filter === "active" ? "Nessun voucher attivo" : "Nessun voucher trovato"}
          </h3>
          <p className="text-zinc-400 text-sm">
            {filter === "active" 
              ? "Riscatta i tuoi punti nella sezione Punti per ottenere voucher!"
              : "Cambia filtro per vedere altri voucher"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVouchers.map((voucher) => {
            const isActive = !voucher.used && new Date(voucher.expires_at) > new Date();
            
            return (
              <div 
                key={voucher.voucher_id}
                className={`relative overflow-hidden rounded-lg border ${
                  isActive 
                    ? "border-primary/50 bg-zinc-900/80" 
                    : "border-zinc-800 bg-zinc-900/50 opacity-70"
                }`}
              >
                {/* Ticket decoration */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-primary/10 rounded-bl-full" />
                
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center">
                      {getVoucherIcon(voucher.type)}
                    </div>
                    {getStatusBadge(voucher)}
                  </div>

                  <h3 className="text-white font-semibold mb-1">{voucher.description}</h3>
                  
                  <div className="flex items-center gap-2 mb-4">
                    {voucher.type === "discount_percent" ? (
                      <span className="text-2xl font-bold text-primary">-{voucher.value}%</span>
                    ) : voucher.type === "voucher" ? (
                      <span className="text-2xl font-bold text-green-400">€{voucher.value}</span>
                    ) : (
                      <span className="text-lg font-bold text-purple-400">Premio Fisico</span>
                    )}
                  </div>

                  {/* Voucher Code */}
                  <div className="bg-zinc-800/50 rounded p-3 mb-4">
                    <p className="text-xs text-zinc-500 mb-1">Codice Voucher</p>
                    <div className="flex items-center justify-between">
                      <code className="text-primary font-mono text-sm">
                        {voucher.voucher_id.replace("voucher_", "").toUpperCase()}
                      </code>
                      {isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyCode(voucher.voucher_id)}
                          className="h-7 px-2"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Creato: {new Date(voucher.created_at).toLocaleDateString("it-IT")}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Scade: {new Date(voucher.expires_at).toLocaleDateString("it-IT")}
                    </div>
                  </div>

                  {voucher.used && voucher.used_at && (
                    <div className="mt-3 pt-3 border-t border-zinc-800 text-xs text-zinc-500">
                      Utilizzato il {new Date(voucher.used_at).toLocaleDateString("it-IT")}
                      {voucher.used_on_order && ` - Ordine ${voucher.used_on_order}`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-blue-400 font-medium mb-1">Come utilizzare i voucher</h4>
            <ul className="text-zinc-400 text-sm space-y-1">
              <li>• I voucher sconto vengono applicati automaticamente al checkout degli ordini</li>
              <li>• I buoni valore possono essere usati per pagare parzialmente o totalmente un ordine</li>
              <li>• Ogni voucher può essere utilizzato una sola volta</li>
              <li>• I voucher hanno una scadenza di 3 mesi dalla data di riscatto</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
